import { Array, Match, Option, Order, Schema } from 'effect';

import { compareTokens } from './compareTokens';
import {
  FacetKey,
  facetKeyEquivalence,
  facetKeyOrder,
  type FacetKey as FacetKeyType,
} from './facetKey';
import { ListingScope } from './listingScope';

export const ListingSort = Schema.Struct({
  field: Schema.Literals(['none', 'price', 'name', 'created']),
  direction: Schema.Literals(['asc', 'desc']),
});

export type ListingSort = typeof ListingSort.Type;

export const TermSelection = Schema.Struct({
  key: FacetKey,
  values: Schema.NonEmptyArray(Schema.String),
});

export type TermSelection = typeof TermSelection.Type;

export const RangeSelection = Schema.Struct({
  key: FacetKey,
  min: Schema.NullOr(Schema.Finite),
  max: Schema.NullOr(Schema.Finite),
});

export type RangeSelection = typeof RangeSelection.Type;

export const ListingSelection = Schema.Struct({
  terms: Schema.Array(TermSelection),
  ranges: Schema.Array(RangeSelection),
  sort: ListingSort,
});

export type ListingSelection = typeof ListingSelection.Type;

export const ListingState = Schema.Struct({
  scope: ListingScope,
  selection: ListingSelection,
  page: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});

export type ListingState = typeof ListingState.Type;

export const ListingChange = Schema.TaggedUnion({
  ToggleTerm: { key: FacetKey, value: Schema.String },
  SetTerms: { key: FacetKey, values: Schema.Array(Schema.String) },
  SetRange: {
    key: FacetKey,
    min: Schema.NullOr(Schema.Finite),
    max: Schema.NullOr(Schema.Finite),
  },
  ClearKey: { key: FacetKey },
  ClearAll: {},
  SetSort: { sort: ListingSort },
  SetPage: { page: Schema.Int },
});

export type ListingChange = typeof ListingChange.Type;

export const emptySelection: ListingSelection = {
  terms: [],
  ranges: [],
  sort: { field: 'none', direction: 'asc' },
};

const normalizeRange = (range: RangeSelection): RangeSelection | undefined => {
  const { key } = range;
  let { min, max } = range;

  if (min !== null && max !== null && min > max) {
    const previousMin = min;
    min = max;
    max = previousMin;
  }

  if (min === null && max === null) return undefined;
  return { key, min, max };
};

/** A missing range bound orders before every number. */
const boundaryOrder: Order.Order<number | null> = Order.mapInput(
  Option.makeOrder(Order.Number),
  Option.fromNullOr
);

const rangeOrder: Order.Order<RangeSelection> = Order.combineAll([
  Order.mapInput(facetKeyOrder, (range: RangeSelection) => range.key),
  Order.mapInput(boundaryOrder, (range: RangeSelection) => range.min),
  Order.mapInput(boundaryOrder, (range: RangeSelection) => range.max),
]);

/** Normalizes filter identity, values and ranges before a port reads a selection. */
export const canonicalSelection = (selection: ListingSelection): ListingSelection => {
  const orderedTerms = selection.terms.toSorted((left, right) =>
    facetKeyOrder(left.key, right.key)
  );
  const terms = Array.isReadonlyArrayNonEmpty(orderedTerms)
    ? Array.groupWith(orderedTerms, (left, right) =>
        facetKeyEquivalence(left.key, right.key)
      ).flatMap((group) => {
        const values = Array.dedupe(group.flatMap((term) => term.values)).toSorted(compareTokens);
        return Array.isReadonlyArrayNonEmpty(values) ? [{ key: group[0].key, values }] : [];
      })
    : [];

  const orderedRanges = selection.ranges
    .map(normalizeRange)
    .filter((range): range is RangeSelection => range !== undefined)
    .toSorted(rangeOrder);
  const ranges = Array.isReadonlyArrayNonEmpty(orderedRanges)
    ? Array.groupWith(orderedRanges, (left, right) => facetKeyEquivalence(left.key, right.key)).map(
        (group) => group[0]
      )
    : [];

  return { terms, ranges, sort: selection.sort };
};

const resetPage = (state: ListingState, selection: ListingSelection): ListingState => ({
  scope: state.scope,
  selection: canonicalSelection(selection),
  page: 1,
});

const withoutKey = (selection: ListingSelection, key: FacetKeyType): ListingSelection => ({
  terms: selection.terms.filter((term) => !facetKeyEquivalence(term.key, key)),
  ranges: selection.ranges.filter((range) => !facetKeyEquivalence(range.key, key)),
  sort: selection.sort,
});

/** Applies one listing command and returns a canonical state. */
export const applyChange = (state: ListingState, change: ListingChange): ListingState =>
  Match.value(change).pipe(
    Match.tag('ToggleTerm', ({ key, value }) => {
      const existing = state.selection.terms.find((term) => facetKeyEquivalence(term.key, key));
      const values = existing === undefined ? [] : [...existing.values];
      const valueIndex = values.indexOf(value);

      if (valueIndex === -1) values.push(value);
      else values.splice(valueIndex, 1);

      const remaining = withoutKey(state.selection, key);

      return resetPage(state, {
        ...remaining,
        terms: Array.isReadonlyArrayNonEmpty(values)
          ? [...remaining.terms, { key, values }]
          : remaining.terms,
      });
    }),
    Match.tag('SetTerms', ({ key, values }) => {
      const remaining = withoutKey(state.selection, key);

      return resetPage(state, {
        ...remaining,
        terms: Array.isReadonlyArrayNonEmpty(values)
          ? [...remaining.terms, { key, values }]
          : remaining.terms,
      });
    }),
    Match.tag('SetRange', ({ key, min, max }) => {
      const remaining = withoutKey(state.selection, key);

      return resetPage(state, {
        ...remaining,
        ranges: [...remaining.ranges, { key, min, max }],
      });
    }),
    Match.tag('ClearKey', ({ key }) => resetPage(state, withoutKey(state.selection, key))),
    Match.tag('ClearAll', () =>
      resetPage(state, { ...emptySelection, sort: state.selection.sort })
    ),
    Match.tag('SetSort', ({ sort }) => resetPage(state, { ...state.selection, sort })),
    Match.tag('SetPage', ({ page }) => ({
      scope: state.scope,
      selection: canonicalSelection(state.selection),
      page: Math.max(1, page),
    })),
    Match.exhaustive
  );
